import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { AlertCircle, Check, GitBranch, Info, MapPin, RotateCcw, ShieldCheck, Users, X, Zap } from "lucide-react";
import { components } from "react-select";
import CommonButton from "../../common/CommonButton";
import ReactSelector from "../../common/CommonReactSelector";
import { getAllGeoCode, urgentAssign } from "../../../service/Configuration";
import { toast } from "../../../utils/toast";
import { apiErrorMessage } from "../../../utils/apiError";
import { getPermissionByScreenCode } from "../../../utils/permissions";
import { fetchFieldAgentMapProject } from "../../../service/Task";
import AppContext from "../../../context/AppContext";
import translations from "../../common/Translations";
import { isRTL } from "../../../utils/rtl";

const Placeholder = (props) => <components.Placeholder {...props} />;

const ALL = "ALL";
const ERROR_BORDER = "#E4587A";

const emptySelection = () => ({
  workflowIds: [],
  allWorkflows: false,
  areaCodes: [],
  allAreaCodes: false,
  userIds: [],
  allUsers: false,
});

// Multi-select "All" handling, same behavior as the Geo Codes picker in
// NewRuleModal.js: picking "All" (or ticking every option by hand) turns the
// all-flag on; unticking one option while "All" is on keeps every other
// option selected and turns the flag off.
const resolveMultiWithAll = ({ picked, actionMeta, optionValues, wasAll }) => {
  const list = picked || [];
  const hasAll = list.some((opt) => opt.value === ALL);

  if (wasAll && hasAll) {
    const unticked = actionMeta?.option?.value ?? actionMeta?.removedValue?.value;
    if (unticked !== undefined && unticked !== ALL) {
      return { values: optionValues.filter((v) => v !== unticked), all: false };
    }
    return { values: [], all: true };
  }

  const pickedValues = list.filter((opt) => opt.value !== ALL).map((opt) => opt.value);
  const pickedEvery =
    optionValues.length > 0 && optionValues.every((v) => pickedValues.includes(v));
  const isAll = (hasAll && !wasAll) || pickedEvery;
  return { values: isAll ? [] : pickedValues, all: isAll };
};

// Urgent assign takes active field agents only. The project agent list has no
// documented status field, so drop an agent only when it is explicitly flagged
// inactive; the backend still rejects any invalid agent with code 601.
const isActiveAgent = (agent) => {
  if (!agent || agent.userId == null) return false;
  if (agent.isDeleted === true || agent.isDeleted === 1) return false;
  if (agent.isActive === false || agent.isActive === 0) return false;
  if (agent.active === false || agent.active === 0) return false;
  const status = typeof agent.status === "string" ? agent.status.toUpperCase() : null;
  return !status || status === "ACTIVE";
};

// Numbered step marker: blue number while pending, green check once the
// section has a selection, rose when submit was attempted without one.
function StepBadge({ number, done, error }) {
  if (done) {
    return (
      <span className="h-7 w-7 shrink-0 rounded-full flex items-center justify-center bg-[#E7F6ED] text-[#188A4B]">
        <Check size={15} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span
      className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
        error ? "bg-rose-50 text-rose-600" : "bg-[#E0EBF4] text-[#236DB4]"
      }`}
    >
      {number}
    </span>
  );
}

export default function UrgentAssignmentModal({ open, setOpen, workflowOptions = [] }) {
  const { projectId, langMode } = useContext(AppContext);
  const t = translations[langMode] || translations.en;
  const rtl = isRTL(langMode);

  const [selection, setSelection] = useState(emptySelection);
  const [geoCodes, setGeoCodes] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // "form" while picking values, "confirm" for the review step before the call.
  const [step, setStep] = useState("form");
  // Message from a rejected request (code 601 names the invalid workflow or
  // agents); shown inline on the form, cleared once the selection changes.
  const [apiError, setApiError] = useState("");

  // Load geo codes and field agents only while the modal is open -- same
  // sources NewRuleModal.js uses for its Geo Codes and Assign To pickers.
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoadingOptions(true);
    Promise.all([getAllGeoCode(projectId), fetchFieldAgentMapProject(projectId)])
      .then(([geoData, agentData]) => {
        if (cancelled) return;
        setGeoCodes((Array.isArray(geoData) ? geoData : []).filter(Boolean));
        setAgentList(
          (agentData?.body?.value || []).filter(isActiveAgent).map((agent) => ({
            value: agent.userId,
            label: agent.userName,
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to load urgent assignment options:", error);
        if (!cancelled) {
          setGeoCodes([]);
          setAgentList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const allOption = useMemo(() => ({ value: ALL, label: t.All || "All" }), [t]);
  const areaCodeOptions = useMemo(
    () => geoCodes.map((code) => ({ value: code, label: code })),
    [geoCodes]
  );

  const handleMultiChange = (valuesKey, allKey, options) => (picked, actionMeta) => {
    setApiError("");
    setSelection((prev) => {
      const { values, all } = resolveMultiWithAll({
        picked,
        actionMeta,
        optionValues: options.map((opt) => opt.value),
        wasAll: prev[allKey],
      });
      return { ...prev, [valuesKey]: values, [allKey]: all };
    });
  };

  const clearField = (valuesKey, allKey) => () => {
    setApiError("");
    setSelection((prev) => ({ ...prev, [valuesKey]: [], [allKey]: false }));
  };

  const fields = [
    {
      key: "workflows",
      Icon: GitBranch,
      label: t.sarWorkflow || "Workflow",
      help: t.urgentWorkflowHelp || "Urgent tasks from these workflows will be assigned.",
      placeholder: t.selectWorkflow || "Select Workflow",
      error: t.urgentSelectWorkflowRequired || "Please select at least one workflow",
      options: workflowOptions,
      values: selection.workflowIds,
      isAll: selection.allWorkflows,
      onChange: handleMultiChange("workflowIds", "allWorkflows", workflowOptions),
      onClear: clearField("workflowIds", "allWorkflows"),
      isLoading: false,
    },
    {
      key: "areaCodes",
      Icon: MapPin,
      label: t.urgentAreaCodes || "Area Codes",
      help: t.urgentAreaCodeHelp || "Only tasks located in these area codes are included.",
      placeholder: loadingOptions
        ? t.LoadingGeo || "Loading Geo Codes..."
        : t.urgentSelectAreaCodes || "Select area codes",
      error: t.urgentSelectAreaCodeRequired || "Please select at least one area code",
      options: areaCodeOptions,
      values: selection.areaCodes,
      isAll: selection.allAreaCodes,
      onChange: handleMultiChange("areaCodes", "allAreaCodes", areaCodeOptions),
      onClear: clearField("areaCodes", "allAreaCodes"),
      isLoading: loadingOptions,
    },
    {
      key: "agents",
      Icon: Users,
      label: t.urgentFieldAgents || "Field Agents",
      help: t.urgentAgentHelp || "The selected tasks are distributed among these agents.",
      placeholder: t.urgentSelectFieldAgents || "Select field agents",
      error: t.urgentSelectAgentRequired || "Please select at least one field agent",
      options: agentList,
      values: selection.userIds,
      isAll: selection.allUsers,
      onChange: handleMultiChange("userIds", "allUsers", agentList),
      onClear: clearField("userIds", "allUsers"),
      isLoading: loadingOptions,
    },
  ].map((field) => {
    const count = field.isAll ? field.options.length : field.values.length;
    return { ...field, count, done: field.isAll || field.values.length > 0 };
  });

  const doneCount = fields.filter((f) => f.done).length;
  const isComplete = doneCount === fields.length;

  const countLabel = (field) =>
    field.isAll
      ? `${t.All || "All"} (${field.count})`
      : `${field.count} ${(t.selected || "selected").trim()}`;

  const resetForm = () => {
    setSelection(emptySelection());
    setShowErrors(false);
    setApiError("");
    setStep("form");
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
    resetForm();
  };

  const handleReset = resetForm;

  const hasUpdateAccess = () => {
    // Same gate as the Urgent Task Assignment button: Update access on the
    // Special Assignment Rules screen (super admin / fullAccess pass).
    if (getPermissionByScreenCode("SPECIAL_ASSIGNMENT_RULES")?.canUpdate) return true;
    toast.error(t.urgentNoPermission || "You don't have permission to assign urgent tasks");
    return false;
  };

  // The API takes explicit lists, so "All" expands to every loaded option.
  const payload = {
    projectId,
    workflowIds: selection.allWorkflows
      ? workflowOptions.map((opt) => opt.value)
      : selection.workflowIds,
    areaCodes: selection.allAreaCodes ? [...geoCodes] : selection.areaCodes,
    fieldAgentIds: selection.allUsers ? agentList.map((opt) => opt.value) : selection.userIds,
  };

  // Step 1: validate the form, then move to the confirm step.
  const handleReview = () => {
    if (submitting || !hasUpdateAccess()) return;
    if (!isComplete) {
      setShowErrors(true);
      return;
    }
    setApiError("");
    setStep("confirm");
  };

  // Step 2: send the request. The backend always answers HTTP 200 and queues
  // the work: 600 means accepted (no counts; the requester gets an "Urgent
  // Assignment" notification when it finishes), 601 means rejected and the
  // message names the invalid workflow or agents.
  const handleConfirm = async () => {
    if (submitting || !hasUpdateAccess()) return;
    const fallbackError = t.urgentAssignFailed || "Failed to assign urgent tasks";

    setSubmitting(true);
    let accepted = false;
    try {
      const response = await urgentAssign(payload);
      if (response?.header?.code === 600) {
        accepted = true;
        const message = response?.body?.value;
        toast.success(
          typeof message === "string" && message.trim()
            ? message
            : t.urgentAssignSuccess ||
                "Urgent assignment is being processed in the background. You'll be notified once it's complete."
        );
      } else {
        const message = apiErrorMessage(response, fallbackError);
        setApiError(message);
        toast.error(message);
      }
    } catch (error) {
      const message = apiErrorMessage(error?.response?.data, fallbackError);
      setApiError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }

    if (accepted) {
      setOpen(false);
      resetForm();
    } else {
      // Keep the form open with the selection intact so it can be fixed.
      setStep("form");
    }
  };

  const agentCount = payload.fieldAgentIds.length;
  const confirmMessage = (
    t.urgentConfirmMessage ||
    "Add {count} agent(s) to all open tasks in the selected activities and areas? Existing assignees are kept."
  ).replace("{count}", agentCount);

  const helpPoints = [
    t.urgentHelpKeepAssignees || "Existing assignees are kept; the agents are added on top.",
    t.urgentHelpSkipCompleted || "Completed and Finished tasks are skipped.",
    t.urgentHelpOneTime || "It's one-time: new uploads won't go to these agents automatically.",
    t.urgentHelpInProgress || "In-Progress tasks also get the agents, and the task list shows them alongside the existing assignees.",
  ];

  const summaryGrid = (
    <div className="grid grid-cols-3 gap-2">
      {fields.map((field) => (
        <div key={field.key} className="rounded-lg bg-white border border-slate-100 px-2.5 py-2 min-w-0">
          <p className="text-[11px] text-slate-500 truncate">{field.label}</p>
          <p className={`text-sm font-bold mt-0.5 truncate ${field.done ? "text-slate-800" : "text-slate-300"}`}>
            <span className="rtl-keep-ltr">
              {field.done ? (field.isAll ? `${t.All || "All"} · ${field.count}` : field.count) : "—"}
            </span>
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <Transition appear show={open} as={Fragment}>
      {/* While the urgent-assign call is pending, outside clicks and Esc are
          ignored so the request can't be abandoned mid-way. */}
      <Dialog as="div" className="relative z-50" onClose={submitting ? () => {} : handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>
        <div className="fixed inset-0 z-0 flex items-center justify-center p-3 sm:p-4 overflow-visible">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel
              dir={rtl ? "rtl" : "ltr"}
              aria-busy={submitting}
              className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl transform transition-all flex flex-col overflow-hidden"
              style={{ maxHeight: "88vh" }}
              data-testid="urgent-assignment-modal"
            >
              {/* Header */}
              <div className="shrink-0 flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-slate-100">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center bg-[#E0EBF4] text-[#236DB4]">
                    <Zap size={20} fill="currentColor" />
                  </div>
                  <div className="min-w-0">
                    <Dialog.Title className="text-base font-bold text-slate-800 leading-tight sm:text-lg">
                      {t.urgentTaskAssignment || "Urgent Task Assignment"}
                    </Dialog.Title>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t.urgentTaskAssignmentSubtitle ||
                        "Choose the workflows, area codes and field agents for urgent task assignment."}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={submitting}
                  aria-label={t.close || "Close"}
                  className="h-8 w-8 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                >
                  <X size={18} />
                </button>
              </div>

              {step === "confirm" ? (
                <>
                  {/* Confirm step */}
                  <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-6" data-testid="urgent-assignment-confirm">
                    <div className="flex flex-col items-center text-center">
                      <div className="h-14 w-14 rounded-full flex items-center justify-center bg-[#E0EBF4] text-[#236DB4] mb-3">
                        <ShieldCheck size={28} />
                      </div>
                      <p className="text-base font-bold text-slate-800">
                        {t.urgentConfirmTitle || "Confirm urgent assignment"}
                      </p>
                      <p className="text-sm text-slate-600 mt-1.5 max-w-md" data-testid="urgent-assignment-confirm-message">
                        {confirmMessage}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 sm:p-4 mt-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-slate-400 mb-2">
                        {t.urgentSummary || "Assignment summary"}
                      </p>
                      {summaryGrid}
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100">
                    <div className="flex items-center">
                      {submitting && (
                        <span className="inline-flex items-center gap-2 text-xs font-medium text-[#236DB4]" role="status">
                          <span className="h-3.5 w-3.5 rounded-full border-2 border-[#236DB4] border-t-transparent animate-spin" />
                          {t.urgentPleaseWait || "Assigning urgent tasks, please wait..."}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <CommonButton
                        variant="outline"
                        onClick={() => setStep("form")}
                        disabled={submitting}
                        className="!h-10 !rounded-[10px]"
                        data-testid="urgent-assignment-back"
                      >
                        {t.urgentConfirmBack || "Back"}
                      </CommonButton>
                      <CommonButton
                        colorIntent="primary"
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={submitting}
                        className="!h-10 !rounded-[10px]"
                        data-testid="urgent-assignment-confirm-submit"
                      >
                        <Zap size={16} />{" "}
                        {submitting ? t.urgentAssigning || "Assigning..." : t.urgentConfirm || "Yes, assign"}
                      </CommonButton>
                    </div>
                  </div>
                </>
              ) : (
              <>
              {/* Body */}
              <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-3">
                {apiError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700"
                    data-testid="urgent-assignment-error"
                  >
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <span className="min-w-0 break-words">{apiError}</span>
                  </div>
                )}

                {/* How it works */}
                <div className="rounded-xl border border-[#BFD5EA] bg-[#F7FAFD] px-3 py-2.5 sm:px-4" data-testid="urgent-assignment-help">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-[#236DB4]">
                    <Info size={14} className="shrink-0" />
                    {t.urgentHowItWorks || "How it works"}
                  </p>
                  <ul className="mt-1.5 space-y-1 ps-5 list-disc text-xs text-slate-600 marker:text-[#236DB4]">
                    {helpPoints.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>

                {fields.map((field, index) => {
                  const hasError = showErrors && !field.done;
                  return (
                    <section
                      key={field.key}
                      data-testid={`urgent-assignment-${field.key}`}
                      className={`rounded-xl border p-3 sm:p-4 transition-colors ${
                        hasError
                          ? "border-rose-200 bg-rose-50/30"
                          : field.done
                            ? "border-[#BFD5EA] bg-[#F7FAFD]"
                            : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <StepBadge number={index + 1} done={field.done} error={hasError} />
                          <div className="min-w-0">
                            <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                              <field.Icon size={14} className="text-slate-400 shrink-0" />
                              {field.label}
                              <span className="text-rose-500">*</span>
                            </label>
                            <p className="text-xs text-slate-500 mt-0.5">{field.help}</p>
                          </div>
                        </div>
                        {field.done && (
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E0EBF4] text-[#236DB4] whitespace-nowrap">
                              <span className="rtl-keep-ltr">{countLabel(field)}</span>
                            </span>
                            <button
                              type="button"
                              onClick={field.onClear}
                              disabled={submitting}
                              className="text-xs font-medium text-slate-400 hover:text-rose-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-400"
                            >
                              {(t.clear || "Clear").trim()}
                            </button>
                          </div>
                        )}
                      </div>

                      <ReactSelector
                        options={field.options.length ? [allOption, ...field.options] : []}
                        value={field.isAll ? [allOption] : field.options.filter((opt) => field.values.includes(opt.value))}
                        onChange={field.onChange}
                        isOptionSelected={(option, selectValue) =>
                          field.isAll ||
                          (selectValue || []).some((item) => item.value === option.value)
                        }
                        closeMenuOnSelectValue={ALL}
                        noOptionsMessage={() => t.nodatavailvle ?? "No data available"}
                        components={{ Placeholder }}
                        placeholder={field.placeholder}
                        isLoading={field.isLoading}
                        isDisabled={submitting}
                        isSearchable
                        isMulti
                        isClearable={false}
                        height="44px"
                        fontSize="14px"
                        borderColor={hasError ? ERROR_BORDER : undefined}
                        overflow="overlay"
                        selectedValuesMaxHeight="64px"
                        menuPortalTarget={document.body}
                        dataTestId={`urgent-assignment-${field.key}-select`}
                      />

                      {hasError && (
                        <p className="flex items-center gap-1.5 text-xs text-rose-600 mt-2">
                          <AlertCircle size={13} className="shrink-0" />
                          {field.error}
                        </p>
                      )}
                    </section>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">
                    <span className="rtl-keep-ltr font-semibold text-slate-700">{doneCount}/{fields.length}</span>{" "}
                    {t.urgentStepsComplete || "steps complete"}
                  </span>
                  {doneCount > 0 && !submitting && (
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={submitting}
                      className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-500"
                    >
                      <RotateCcw size={12} /> {(t.reset || "Reset").trim()}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <CommonButton variant="outline" onClick={handleClose} disabled={submitting} className="!h-10 !rounded-[10px]">
                    {t.cancle}
                  </CommonButton>
                  <CommonButton
                    colorIntent="primary"
                    variant="primary"
                    onClick={handleReview}
                    disabled={submitting || loadingOptions}
                    className="!h-10 !rounded-[10px]"
                    data-testid="urgent-assignment-submit"
                  >
                    <Zap size={16} /> {t.urgentAssignNow || "Assign Urgent Tasks"}
                  </CommonButton>
                </div>
              </div>
              </>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
